import { useEffect, useRef } from "react";
import { scrollWizardToTop } from "@/lib/scrollWizardToTop";

/**
 * Scrolls the page/scroll-container back to the top whenever the visible step
 * of a multi-step workflow changes (Next, Back, step jump, draft restore or a
 * validation redirect).
 *
 * Scroll only — no form state, validation, navigation or focus behaviour is
 * changed beyond moving focus to the optional step heading for accessibility.
 * The first render is skipped so autofocus on entry is never interrupted.
 */
export function useScrollToTopOnStepChange(
  stepKey: string | number,
  options: { root?: HTMLElement | null; focusTarget?: HTMLElement | null } = {},
) {
  const previousKey = useRef<string | number | null>(null);
  const { root, focusTarget } = options;

  useEffect(() => {
    if (previousKey.current === null) {
      previousKey.current = stepKey;
      return;
    }
    if (previousKey.current === stepKey) return;
    previousKey.current = stepKey;

    // Scroll after the new step has rendered. Late-arriving content (images,
    // async step data) and iOS scroll anchoring can restore the old offset
    // after a single pass, so re-assert the top position for a short window.
    const timers: ReturnType<typeof setTimeout>[] = [];
    const frame = requestAnimationFrame(() => {
      scrollWizardToTop({ root, focusTarget });
      [80, 200, 400].forEach((delay) => {
        timers.push(
          setTimeout(() => {
            const scrolled =
              (typeof window !== "undefined" && window.scrollY > 0) ||
              (typeof document !== "undefined" &&
                Array.from(
                  document.querySelectorAll<HTMLElement>(
                    "[data-scroll-container],main,[data-radix-scroll-area-viewport]",
                  ),
                ).some((el) => el.scrollTop > 0));
            if (scrolled) scrollWizardToTop({ root });
          }, delay),
        );
      });
    });
    return () => {
      cancelAnimationFrame(frame);
      timers.forEach(clearTimeout);
    };
  }, [stepKey, root, focusTarget]);

}
